/**
 * SummaryCardRow - Single card entry row for summary view
 *
 * Uses table row layout for consistent column alignment across all rows.
 * Displays: quantity, card name (clickable), mana cost symbols, price
 */
import type { ReactElement } from 'react';

import clsx from 'clsx';

import CardHoverPreview from '@/components/CardHoverPreview/CardHoverPreview';
import SplitManaCost from '@/components/ManaSymbol/SplitManaCost';
import { getDisplayImageUris, getDisplayManaCost, getRarityTextClass } from '@/lib/cardUtils';
import { formatPrice } from '@/lib/price';
import type { Currency } from '@/types/card';

import type { SummaryCard } from './SummarySection';

export interface SummaryCardRowProps {
  /** Card data from database */
  card: SummaryCard;
  /** Number of copies in deck */
  quantity: number;
  /** Card price (single copy, from full card cache) */
  price: number | null;
  /** Whether to show price column */
  showPrice?: boolean;
  /** Whether user owns this card (for price coloring) */
  isOwned?: boolean;
  /** Called when card name is clicked */
  onCardClick?: (scryfallId: string) => void;
  /** Currency for price display */
  currency?: Currency;
}

export default function SummaryCardRow({
  card,
  quantity,
  price,
  showPrice = true,
  isOwned = false,
  onCardClick,
  currency = 'USD',
}: SummaryCardRowProps): ReactElement {
  const totalPrice = price !== null ? price * quantity : null;
  const imageUris = getDisplayImageUris(card);
  const manaCost = getDisplayManaCost(card);

  return (
    <tr className="text-sm">
      {/* Quantity - dimmed if 1 */}
      <td className={clsx('py-0.5 pr-1.5 text-right font-mono tabular-nums', quantity === 1 ? 'text-zinc-600' : 'text-zinc-400')}>
        {quantity}
      </td>

      {/* Card Name - clickable with hover preview */}
      <td className="max-w-0 truncate py-0.5 pr-2 lg:max-w-none">
        <CardHoverPreview imageUris={imageUris} name={card.name} layout={card.layout} cardFaces={card.card_faces}>
          <button
            type="button"
            onClick={() => onCardClick?.(card.id)}
            className={clsx('hover:text-aether block max-w-full truncate text-left transition-colors', getRarityTextClass(card.rarity))}
          >
            {card.name}
          </button>
        </CardHoverPreview>
      </td>

      {/* Mana Cost */}
      <td className="py-0.5 pr-2 text-right whitespace-nowrap">{manaCost ? <SplitManaCost cost={manaCost} shadow={false} /> : null}</td>

      {/* Price */}
      {showPrice && (
        <td
          className={clsx(
            'py-0.5 text-right font-mono text-xs whitespace-nowrap tabular-nums',
            isOwned ? 'text-emerald-400' : 'text-zinc-500',
          )}
        >
          {totalPrice !== null ? formatPrice(totalPrice, currency) : '—'}
        </td>
      )}
    </tr>
  );
}
